import { Controller } from "@hotwired/stimulus";

let select2Loaded = false;

export default class Select2Controller extends Controller {
  static targets = [
    "selectBackskin",
    "selectAnalytics",
    "moveFolder",
    "selectInterest",
  ];
  static values = {
    frameId: { type: String },
  };
  selected = [];

  connect() {
    if ($(this.element).hasClass("select2-hidden-accessible")) {
      $(this.element).parent()?.find(".select2-container--default")?.remove();
    }

    this.loadSelect2()
      .then(() => {
        this.initSelect2();
        this.hideSkeleton();
      })
      .catch((error) => {
        console.error("Failed to load Select2:", error);
      });
  }

  hideSkeleton() {
    const wrapper = this.element.closest(".select2-loading");
    if (wrapper) {
      wrapper.classList.remove("select2-loading");
    }
  }

  disconnect() {
    this.stopOuterScrollClose();
    this.hoverInstance?.$results.off(".itoonsHover");
    this.hoverInstance?.$container.off(".itoonsHover");
    this.hoverInstance?.dropdown?.$dropdown.off(".itoonsHover");
    if ($(this.element).hasClass("select2-hidden-accessible")) {
      $(this.element).select2("destroy");
    }
  }

  async loadSelect2() {
    if (select2Loaded) return;

    const module = await import("select2");
    const select2 = module.default || module;

    select2($);
    select2Loaded = true;
  }

  initSelect2() {
    const $element = $(this.element);
    const $dropdownParent =
      this.hasFrameIdValue && this.frameIdValue.trim() !== ""
        ? this.element.closest(`${this.frameIdValue}`)
        : $element.parent();
    const dropdownOptions = {
      placeholder: this.hasSelectBackskinTarget
        ? "Select"
        : this.element.dataset.placeholder || "Search Interest",
      dropdownParent: $dropdownParent,
    };
    if (["custom_lang", "message_custom_lang"].includes(this.element.id)) {
      dropdownOptions.language = {
        noResults: () => this.element.dataset.languagesLoading === "1"
          ? "Loading languages…"
          : "No results found",
      };
    }
    if (this.element.dataset.prioritizePrefixMatches === "true") {
      dropdownOptions.sorter = (results) => {
        const term = document
          .querySelector(".select2-container--open .select2-search__field")
          ?.value.trim().toLocaleLowerCase();

        if (!term) return results;

        return results
          .map((result, index) => ({ result, index }))
          .sort((first, second) => {
            const firstStartsWith = (first.result.text || "")
              .trim().toLocaleLowerCase().startsWith(term);
            const secondStartsWith = (second.result.text || "")
              .trim().toLocaleLowerCase().startsWith(term);

            return Number(secondStartsWith) - Number(firstStartsWith) ||
              first.index - second.index;
          })
          .map(({ result }) => result);
      };
    }
    if (this.element.dataset.closeOnSelect === "false") {
      dropdownOptions.closeOnSelect = false;
    }
    if (this.element.dataset.searchByCode === "true") {
      dropdownOptions.matcher = (params, data) => {
        const term = $.trim(params.term).toLowerCase();

        if (!term) return data;

        const text = (data.text || "").toLowerCase();
        const value = (data.id || "").toLowerCase();

        return text.includes(term) || value.includes(term)
          ? data
          : null;
      };
    }

    $element
      .select2(dropdownOptions)
      .on("select2:open", () => {
        const select2Instance = $element.data("select2");
        if (select2Instance) this.closeOnOuterScroll(select2Instance);
        if (this.element.dataset.filterRemoved === "true") {
          this.searchDisabled();
        }
        this.dropdownOpenTimer = setTimeout(() => {
          this.dropdownOpenTimer = null;
          if (!this.element.isConnected || !select2Instance?.isOpen()) return;

          const dropdown = select2Instance.dropdown.$dropdown[0];
          const searchInput = dropdown.querySelector(".select2-search__field");
          const results = select2Instance.$results?.[0];
          if (results && this.element.dataset.resetScrollOnOpen !== "false") {
            results.scrollTop = 0;
          }
          const placeholder = this.element.dataset.placeholder;
          if (searchInput && placeholder && ["custom_lang", "message_custom_lang"].includes(this.element.id)) {
            searchInput.placeholder = placeholder;
          }
          if (searchInput && !searchInput.disabled) {
            searchInput.focus({ preventScroll: true });
          }
        }, 0);
      })
      .on("select2:close", () => {
        this.stopOuterScrollClose();
        if (this.element.dataset.filterRemoved === "true") {
          this.searchDisabled();
        }
      })
      .on("select2:select", (e) => {
        // Clear the search input
        const searchInput = document.querySelector(
          ".select2-container--open .select2-search__field",
        );
        if (searchInput) {
          searchInput.value = "";
          searchInput.dispatchEvent(new Event("input", { bubbles: true }));
        }

        this.element.dispatchEvent(new Event("change", { bubbles: true }));
        if (this.hasSelectAnalyticsTarget) {
          this.handleSubmitSelection(e);
          return;
        }
        if (this.hasMoveFolderTarget) {
          this.handleFolderSelection(e);
          return;
        }
        if (this.element.dataset.analyticsChart === "true") {
          this.handleUpdateChart();
          return;
        }
        if (this.element.dataset.cartoonizeChart === "true") {
          this.handleUpdateCartoonizeChart();
          return;
        }
        if (this.hasSelectInterestTarget) {
          this.fetchData();
        }
      })
      .on("select2:selecting", (e) => {
        const maxSelection = Number.parseInt(this.element.dataset.maxSelection, 10);
        if (!maxSelection) return;

        const selectedValues = $(this.element).val() || [];
        if (selectedValues.length >= maxSelection) {
          e.preventDefault();

          const errorElement = document.querySelector(".language_error");
          if (!errorElement) return;

          errorElement.innerHTML = `You can select up to ${maxSelection} languages.`;
          setTimeout(() => {
            $(this.element).select2("close");
          }, 0);
        }
      })
      .on("select2:unselect", (e) => {
        const maxSelection = Number.parseInt(this.element.dataset.maxSelection, 10);
        const selectedValues = $(this.element).val() || [];

        if (selectedValues.length < maxSelection) {
          const errorElement = document.querySelector(".language_error");
          if (errorElement) {
            errorElement.innerHTML = "";
          }
        }
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
        if (this.hasSelectAnalyticsTarget) {
          this.handleSubmitSelection(e);
          return;
        }
        if (this.element.dataset.analyticsChart === "true") {
          this.handleUpdateChart();
          return;
        }
        if (this.element.dataset.cartoonizeChart === "true") {
          this.handleUpdateCartoonizeChart();
          return;
        }
        if (this.hasSelectInterestTarget) {
          this.fetchData();
        }
      });
    this.bindOptionHover($element.data("select2"));
  }

  bindOptionHover(instance) {
    if (!instance) return;
    this.hoverInstance = instance;
    const optionSelector = ".select2-results__option--selectable";
    instance.$results.on("mouseenter.itoonsHover", optionSelector, event => {
      // Select2 also synthesizes mouseenter for keyboard navigation.
      if (event.originalEvent) this.pointerHighlightedOption = event.currentTarget;
    });
    instance.$results.on("mouseleave.itoonsHover", optionSelector, event => {
      const option = event.currentTarget;
      if (this.pointerHighlightedOption !== option) return;
      this.pointerHighlightedOption = null;
      option.classList.remove("select2-results__option--highlighted");
      option.setAttribute("aria-selected", option.classList.contains("select2-results__option--selected").toString());
      for (const root of [instance.$container[0], instance.dropdown.$dropdown[0]]) {
        for (const field of root.querySelectorAll("[aria-activedescendant]")) {
          if (field.getAttribute("aria-activedescendant") === option.id) {
            field.removeAttribute("aria-activedescendant");
          }
        }
      }
    });
    const preserveKeyboardHighlight = () => { this.pointerHighlightedOption = null; };
    instance.$container.on("keydown.itoonsHover", preserveKeyboardHighlight);
    instance.dropdown.$dropdown.on("keydown.itoonsHover", preserveKeyboardHighlight);
  }

  closeOnOuterScroll(select2Instance) {
    this.stopOuterScrollClose();
    this.outerScrollTargets = $(this.element).parents().add(globalThis);
    this.outerScrollEvent = `scroll.select2.${select2Instance.id}`;

    // Replace Select2's scroll lock. Scrolling the results list does not trigger
    // these listeners, so users can browse options without closing the dropdown.
    this.outerScrollTargets
      .off(this.outerScrollEvent)
      .on(this.outerScrollEvent, () => {
        if (select2Instance.isOpen()) $(this.element).select2("close");
      });
  }

  stopOuterScrollClose() {
    if (this.dropdownOpenTimer != null) {
      clearTimeout(this.dropdownOpenTimer);
      this.dropdownOpenTimer = null;
    }
    if (this.outerScrollTargets && this.outerScrollEvent) {
      this.outerScrollTargets.off(this.outerScrollEvent);
    }
    this.outerScrollTargets = null;
    this.outerScrollEvent = null;
  }

  async handleSubmitSelection(e) {
    const value = e.target.value;
    const url = new URL(this.element.dataset.url, globalThis.location.origin);
    url.searchParams.set("range", value);

    const turboRequest = await this.loadTurboRequest();

    await turboRequest(url, {
      method: "GET",
      turboStream: true,
    });
  }

  async handleUpdateChart() {
    const status = document.getElementById("analytics-bar-status")?.value;
    const year = document.getElementById("analytics-bar-details")?.value;
    const url = new URL(this.element.dataset.url, globalThis.location.origin);

    url.searchParams.set("year", year);
    url.searchParams.set("status", status);
    const turboRequest = await this.loadTurboRequest();

    await turboRequest(url, {
      method: "get",
      turboStream: true,
    });
  }

  async handleUpdateCartoonizeChart() {
    const year = this.element.value;
    if (!year) return;

    const frame = $(this.element).closest(".analytics-panel").find("turbo-frame");
    if (frame[0]) {
      frame[0].innerHTML = `
        <div class="d-flex justify-content-center align-items-center w-100 py-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      `;
    }

    const url = new URL(
      this.element.dataset.url,
      globalThis.location.origin,
    );

    url.searchParams.set("year", year);
    url.searchParams.set("format", "turbo_stream");
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Failed to load cartoonize chart: ${response.status}`);
    }

    const html = await response.text();

    Turbo.renderStreamMessage(html);
  }

  handleFolderSelection(e) {
    let form = $(this.element).closest("form");
    form[0].requestSubmit();
  }

  fetchData() {
    // remove the selected option from select field and show the placeholder again
    $(".select2-selection__choice").remove();
    $(".select2-search__field")
      .attr("placeholder", "Select Interest")
      .removeAttr("style");

    let form = $(this.element).closest("form");
    form[0].requestSubmit();
  }

  searchDisabled() {
    const instance = $(this.element).data("select2");
    const searchTextarea = instance?.$container?.[0]?.querySelector(".select2-search__field");
    if (!searchTextarea) return;
    // Keep the search field clickable while preventing typing in this selector.
    searchTextarea.removeAttribute("disabled");
    searchTextarea.setAttribute("readonly", "readonly");
  }

  async loadTurboRequest() {
    const turboRequest = (await import("../helpers/api")).turboRequest;
    return turboRequest;
  }
}
